import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';
import {
  KitchenDisplay,
  Organization,
  Store,
  Venue,
} from '@oolio-group/domain';
import { useNavigation } from '@react-navigation/native';
import { useVenues } from '../../hooks/organization/useVenues';
import { useKitchenDisplay } from '../../hooks/useKitchenDisplay';
import { useSession } from '../../hooks/useSession';
import Gradient from '../../component/Gradient/Gradient';
import { useDeviceId } from '../../hooks/useDeviceId';
import styles from './DeviceSelection.styles';
import DeviceList from './DevicesList';
import LocationsList from './LocationsList';
import ButtonIcon from '../../component/Buttons/ButtonIcon';
import {
  POSTHOG_EVENT_ACTIONS,
  POSTHOG_EVENT_DATA,
  analyticsService,
} from '../../analytics/AnalyticsService';
import { Session } from '../../state/preferences';
import OrganizationsList from './OrganizationsList';
import { getAppVersion } from '../../utils/getAppVersion';
import { getPlatform } from '../../utils/getPlatform';
import { useOrgOptions } from '../../hooks/organization/useOrgOptions';

export enum Steps {
  ORGANIZATION = 'Organization',
  STORE = 'Store',
  DEVICE = 'Device',
  PRINTER_PROFILE = 'Printer Profile',
}

const DeviceSelection: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();
  const [step, setStep] = useState<Steps>(Steps.ORGANIZATION);
  // False when the user explicitly navigated back — suppresses the single-option
  // auto-advance so we don't bounce straight forward again. Reset to true on any
  // explicit user selection.
  const shouldAutoSelectFirstOption = useRef(true);
  const { setSession, session } = useSession();
  // Ref so the selection callbacks read the latest session without depending on
  // `session` directly. Depending on `session` gives those callbacks a new
  // identity every render, which re-fires the auto-select effect in an infinite
  // loop on single-option accounts. Mirrors pos-app's Transition screen.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  // Organizations are fetched from the POS GraphQL server (me.organizationPage)
  // so the picker only shows orgs the user is onboarded to on POS, matching
  // the pos-app Transition flow. SSO /userinfo would also include orgs the
  // user has SSO access to but has not been onboarded to POS — those have
  // no venues/stores/devices to select from.
  const { getOrgAsOptionsOfUser } = useOrgOptions();
  const [organizations, setOrganizations] = useState<
    { id: string; name: string }[]
  >([]);
  const [userLoading, setUserLoading] = useState(true);
  const { venues, loading: venueLoading, getVenues } = useVenues();
  const { deviceId } = useDeviceId();

  const {
    kitchenDisplays,
    loading: kitchenDisplayLoading,
    getKitchenDisplays,
    updateKitchenDisplay,
  } = useKitchenDisplay({ storeId: session?.currentStore?.id });

  const setUserToPostHog = (session: Partial<Session>) => {
    // Identify User
    analyticsService.identify(session?.currentOrganization?.id as string, {
      id: session?.currentOrganization?.id,
      name: session?.currentOrganization?.name,
      venue: session?.currentVenue?.name,
      store: session?.currentStore?.name,
    });

    // Identify Organisation
    // NOTE: this pushes to the 'organization' PostHog group, which is a
    // separate group (and property set — venue/store, not features) from
    // pos-app's 'organisation' group. Drift originates from commit
    // f62e93e9d151ddedfebae9bd41cf97f91e8f9480 (#9345), which copy-pasted
    // pos-app's older analytics pattern here without keeping it in sync.
    // Org-property-based flag targeting (e.g. createdAt/isActive, added to
    // pos-app) does not apply to kds-app orgs until this is reconciled.
    analyticsService.group(
      'organization',
      session?.currentOrganization?.id as string,
      {
        id: session?.currentOrganization?.id,
        name: session?.currentOrganization?.name,
        venue: session?.currentVenue?.name,
        store: session?.currentStore?.name,
      },
    );
  };

  const onSelectOrganization = useCallback(
    (organization: Pick<Organization, 'id' | 'name'>) => {
      shouldAutoSelectFirstOption.current = true;
      // Extend the latest session from the ref (not the `session` closure) so
      // this callback keeps a stable identity. setSession + setStep run
      // synchronously (no await on the void return) so they land in one React
      // batch and `step` actually advances past ORGANIZATION.
      //
      // Skip the session write when the desired org already matches the
      // persisted one: a no-op write still produces a new session reference,
      // fans out to every useSession consumer, and on a single-org account
      // feeds the auto-select effect into a render loop (INC-439).
      if (sessionRef.current.currentOrganization?.id !== organization.id) {
        setSession({
          ...sessionRef.current,
          currentOrganization: organization,
        });
      }
      // Fire the venues query with the just-picked org id explicitly, so we
      // don't depend on the auth link reading the new session in time.
      getVenues(organization.id);
      setStep(Steps.STORE);
    },
    [getVenues, setSession],
  );

  const onSelectStore = useCallback(
    (venue: Partial<Venue>, store: Store) => {
      shouldAutoSelectFirstOption.current = true;
      // Compose the post-store session up-front so PostHog identifies the user
      // against the *new* venue/store, not the pre-store snapshot still in
      // sessionRef.current at this point in the call.
      const nextSession = {
        ...sessionRef.current,
        authorized: true,
        currentVenue: venue,
        currentStore: store,
      };
      setSession(nextSession);
      setStep(Steps.DEVICE);
      setUserToPostHog(nextSession);
    },
    [setSession],
  );

  const kitchenDisplayList = useMemo(
    () => Object.values(kitchenDisplays),
    [kitchenDisplays],
  );

  const onSelectKitchenDisplay = useCallback(
    async (kitchenDisplay: KitchenDisplay) => {
      analyticsService.capture(POSTHOG_EVENT_ACTIONS.DEVICE_SELECTED, {
        kdsName: kitchenDisplay.name,
        device: kitchenDisplay?.id,
        printerProfiles: (kitchenDisplay?.printerProfiles || [])?.map(x => ({
          name: x.name,
          id: x.id,
        })),
        orgId: sessionRef.current?.currentOrganization?.id,
        orgName: sessionRef.current?.currentOrganization?.name,
      } as POSTHOG_EVENT_DATA);

      const appVersion = getAppVersion();
      const platform = getPlatform();

      updateKitchenDisplay({
        id: kitchenDisplay.id,
        isPaired: true,
        uuid: deviceId,
        appVersion,
        platform,
      });

      setSession({
        ...sessionRef.current,
        kitchenDisplay: {
          ...kitchenDisplay,
          uuid: deviceId,
          appVersion,
          platform,
        },
      });

      navigation.reset({
        index: 0,
        routes: [{ name: 'MainScreen' }],
      });
    },
    [updateKitchenDisplay, deviceId, navigation, setSession],
  );

  const onBack = useCallback(() => {
    // Explicit back press — suppress the next auto-advance so a single
    // venue/store combo doesn't immediately bounce the user forward again.
    shouldAutoSelectFirstOption.current = false;
    if (step === Steps.STORE) {
      // Multi-org users go back to the org picker; single-org users have
      // nowhere meaningful to go above STORE, so fall through to Login.
      if (organizations.length > 1) {
        setStep(Steps.ORGANIZATION);
      } else {
        navigation.navigate('LoginScreen');
      }
    } else if (step === Steps.DEVICE) {
      setStep(Steps.STORE);
    }
  }, [step, navigation, organizations.length]);

  useEffect(() => {
    session.currentStore?.id && getKitchenDisplays();
  }, [getKitchenDisplays, session.currentStore?.id]);

  // Stream paginated org options into local state. The cancellation token
  // makes useOrgOptions abort recursion if this effect re-runs or unmounts,
  // preventing late batches from a prior fetch from leaking into state.
  useEffect(() => {
    let cancelled = false;
    setOrganizations([]);
    setUserLoading(true);
    getOrgAsOptionsOfUser(
      batch => {
        if (cancelled) return;
        setOrganizations(prev => [...prev, ...batch]);
      },
      () => !cancelled,
    )
      .catch(() => {
        // Treat as "no orgs returned" — the empty list will surface to
        // the user via the OrganizationsList empty state.
      })
      .finally(() => {
        if (!cancelled) setUserLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getOrgAsOptionsOfUser]);

  useEffect(() => {
    if (userLoading) return;
    if (!shouldAutoSelectFirstOption.current) return;
    switch (step) {
      case Steps.ORGANIZATION: {
        if (organizations.length === 1) {
          // onSelectOrganization is now idempotent for the already-current
          // org (no session write, just getVenues + setStep) so calling it
          // unconditionally won't feed the INC-439 render loop.
          onSelectOrganization(organizations[0]);
          break;
        }
        // Only fast-path past the picker if the persisted org is still in
        // the fetched org list. A stale id (user removed from org, etc.)
        // would otherwise skip reselection and fire downstream queries
        // with an invalid org header.
        const persistedOrgId = sessionRef.current.currentOrganization?.id;
        if (
          persistedOrgId &&
          organizations.some(org => org.id === persistedOrgId)
        ) {
          setStep(Steps.STORE);
        }
        break;
      }
      case Steps.STORE:
        if (venues?.length === 1 && venues?.[0]?.stores?.length === 1)
          onSelectStore(venues[0], venues[0].stores[0]);
        break;
      case Steps.DEVICE:
        break;
    }
  }, [
    step,
    venues,
    onSelectStore,
    onSelectOrganization,
    organizations,
    userLoading,
  ]);

  return (
    <Gradient>
      <View style={styles.screen}>
        <View style={styles.modalContainer}>
          <ButtonIcon
            testID="btn-back"
            icon="arrow-left"
            type="cancel"
            onPress={onBack}
            containerStyle={styles.btnBack}
          />
          {step === Steps.ORGANIZATION && (
            <OrganizationsList
              organizations={organizations}
              onSelect={onSelectOrganization}
              loading={userLoading}
            />
          )}
          {step === Steps.STORE && (
            <LocationsList
              venuesData={venues ?? []}
              onSelect={onSelectStore}
              loading={venueLoading}
            />
          )}
          {step === Steps.DEVICE && (
            <DeviceList
              onSelect={onSelectKitchenDisplay}
              loading={kitchenDisplayLoading}
              devices={kitchenDisplayList ?? []}
            />
          )}
        </View>
      </View>
    </Gradient>
  );
};

export default DeviceSelection;
